// Drawer d'édition Configuration - complet ou par bloc (depuis l'onglet Configuration)

import React, { useEffect } from 'react';
import {
  Drawer,
  Form,
  Row,
  Col,
  InputNumber,
  Switch,
  Typography,
  message,
  Alert,
  Divider,
  Button,
  Space,
  Select,
} from 'antd';
import { SettingOutlined } from '@ant-design/icons';
import { useUpdateBusiness } from '../../../hooks/useBusinesses';
import {
  Business,
  DEFAULT_BUSINESS_CONFIGURATION,
  businessSupportsInventoryConfig,
  isFeatureEnabled,
} from '../../../types/business';
import { cleanFirestoreData } from '../../../utils/cleanFirestoreData';
import ExpenseCategoriesStep from './ExpenseCategoriesStep';
import ClientTypesStep from './ClientTypesStep';
import ActivityTypesStep from './ActivityTypesStep';
import OpportunityCategoriesStep from './OpportunityCategoriesStep';

const { Title } = Typography;

/** Blocs éditables — `all` = drawer général. */
export type ConfigEditSection =
  | 'all'
  | 'loyalty'
  | 'marketing'
  | 'messagesClients'
  | 'inventory'
  | 'expenses'
  | 'clientTypes'
  | 'opportunityCategories'
  | 'activityTypes';

const SECTION_TITLES: Record<ConfigEditSection, string> = {
  all: 'Modifier la configuration',
  loyalty: 'Programme de fidélité',
  marketing: 'Marketing',
  messagesClients: 'Messages clients',
  inventory: 'Gestion des stocks',
  expenses: 'Catégories de dépenses',
  clientTypes: 'Types de clients',
  opportunityCategories: "Catégories d'opportunités",
  activityTypes: "Types d'activité",
};

interface EditConfigurationModalProps {
  open: boolean;
  business: Business | null;
  onClose: () => void;
  onSuccess: () => void;
  /** Par défaut `all` (drawer complet). */
  section?: ConfigEditSection;
}

const EditConfigurationModal: React.FC<EditConfigurationModalProps> = ({
  open,
  business,
  onClose,
  onSuccess,
  section = 'all',
}) => {
  const [form] = Form.useForm();
  const updateBusinessMutation = useUpdateBusiness();
  const businessType = business?.type;
  const showInventoryConfig = businessSupportsInventoryConfig(businessType);
  const inventoryFeatureOn = isFeatureEnabled(business, 'inventory');
  const posFeatureOn = isFeatureEnabled(business, 'pos');
  const isFullEdit = section === 'all';

  const showLoyalty = isFullEdit || section === 'loyalty';
  const showMarketing = isFullEdit || section === 'marketing';
  const showMessages = isFullEdit || section === 'messagesClients';
  const showInventory =
    showInventoryConfig &&
    (isFullEdit || section === 'inventory');
  const showExpenses = isFullEdit || section === 'expenses';
  const showClientTypes = isFullEdit || section === 'clientTypes';
  const showOpportunityCategories = isFullEdit || section === 'opportunityCategories';
  const showActivityTypes = isFullEdit || section === 'activityTypes';

  const watchedClientTypes = Form.useWatch('clientTypes', form);
  const watchedOpportunityCategories = Form.useWatch('opportunityCategories', form);
  const watchedActivityTypes = Form.useWatch('activityTypes', form);
  const watchedExpenseCategories = Form.useWatch('expenseCategories', form);
  const watchedMultiSites = Form.useWatch('multiSitesEnabled', form);
  const watchedSalesSiteMode = Form.useWatch('salesSiteMode', form);
  const config = business?.configuration || DEFAULT_BUSINESS_CONFIGURATION;
  const multiSitesOn = watchedMultiSites ?? config.inventory?.multiSitesEnabled ?? false;
  const anySiteSelected =
    (watchedSalesSiteMode ?? config.inventory?.salesSiteMode) === 'any_site';
  const clientTypesValue = watchedClientTypes ?? config.salesActivity?.clientTypes ?? [];
  const opportunityCategoriesValue =
    watchedOpportunityCategories ?? config.salesActivity?.opportunityCategories ?? [];
  const activityTypesValue = watchedActivityTypes ?? config.activityReport?.activityTypes ?? [];
  const expenseCategoriesValue =
    watchedExpenseCategories ?? { categories: config.expenses?.categories ?? [] };

  useEffect(() => {
    if (business && open) {
      const cfg = business.configuration || DEFAULT_BUSINESS_CONFIGURATION;
      form.setFieldsValue({
        loyaltyEnabled: cfg.loyalty?.enabled !== false,
        bonusPercentage: cfg.loyalty?.bonusPercentage ?? 3,
        minimumUsage: cfg.loyalty?.minimumUsage ?? 1000,
        maxActiveCampaigns: cfg.marketing?.maxActiveCampaigns ?? 10,
        messagesClientsDelayMinutes: cfg.messagesClients?.delayMinutes ?? 15,
        messagesClientsAntiSpamHours: cfg.messagesClients?.antiSpamHours ?? 24,
        messagesClientsRequireOptIn: cfg.messagesClients?.requireOptIn ?? true,
        lowStockThreshold: cfg.inventory?.lowStockThreshold ?? 5,
        recalculateCostOnProvisioning: false,
        multiSitesEnabled: cfg.inventory?.multiSitesEnabled ?? false,
        salesSiteMode: cfg.inventory?.salesSiteMode === 'any_site' ? 'any_site' : 'pos_only',
        expenseCategories: { categories: cfg.expenses?.categories ?? [] },
        clientTypes: cfg.salesActivity?.clientTypes ?? [],
        opportunityCategories: cfg.salesActivity?.opportunityCategories ?? [],
        activityTypes: cfg.activityReport?.activityTypes ?? [],
      });
    }
  }, [business, open, form, section]);

  const handleSubmit = async () => {
    if (!business) return;
    try {
      const values = await form.validateFields();
      const cfg = business.configuration || DEFAULT_BUSINESS_CONFIGURATION;
      const clientTypes =
        form.getFieldValue('clientTypes') ?? values.clientTypes ?? cfg.salesActivity?.clientTypes ?? [];
      const opportunityCategories =
        form.getFieldValue('opportunityCategories') ??
        values.opportunityCategories ??
        cfg.salesActivity?.opportunityCategories ??
        [];
      const activityTypes =
        form.getFieldValue('activityTypes') ??
        values.activityTypes ??
        cfg.activityReport?.activityTypes ??
        [];
      const expenseCategories =
        form.getFieldValue('expenseCategories') ??
        values.expenseCategories ??
        { categories: cfg.expenses?.categories ?? [] };

      let configuration: Record<string, unknown> = { ...cfg };

      const applyActivityTypes = () => {
        const activityTypesToSave = Array.isArray(
          form.getFieldValue('activityTypes') ?? activityTypes
        )
          ? form.getFieldValue('activityTypes') ?? activityTypes
          : [];
        configuration.activityReport = {
          ...(typeof cfg.activityReport === 'object' && cfg.activityReport ? cfg.activityReport : {}),
          activityTypes: activityTypesToSave,
        };
      };

      if (isFullEdit) {
        applyActivityTypes();
        const nextLoyaltyEnabled =
          values.loyaltyEnabled !== undefined
            ? values.loyaltyEnabled
            : cfg.loyalty?.enabled ?? true;
        if (nextLoyaltyEnabled && isFeatureEnabled(business, 'loyaltyPass')) {
          message.error(
            'Impossible d’activer la cagnotte : la carte de fidélité BonSpot est active. Désactivez d’abord la feature « Carte de fidélité BonSpot ».'
          );
          return;
        }
        configuration.loyalty = {
            enabled: nextLoyaltyEnabled,
            bonusPercentage: values.bonusPercentage ?? cfg.loyalty?.bonusPercentage ?? 3,
            minimumUsage: values.minimumUsage ?? cfg.loyalty?.minimumUsage ?? 1000,
          };
          configuration.marketing = {
            maxActiveCampaigns:
              values.maxActiveCampaigns !== undefined && values.maxActiveCampaigns !== null
                ? values.maxActiveCampaigns
                : cfg.marketing?.maxActiveCampaigns ?? 10,
          };
          configuration.messagesClients = {
            delayMinutes:
              values.messagesClientsDelayMinutes ?? cfg.messagesClients?.delayMinutes ?? 15,
            antiSpamHours:
              values.messagesClientsAntiSpamHours ?? cfg.messagesClients?.antiSpamHours ?? 24,
            requireOptIn:
              values.messagesClientsRequireOptIn ?? cfg.messagesClients?.requireOptIn ?? true,
            channel: 'whatsapp',
          };
          if (showInventoryConfig) {
            configuration.inventory = {
              ...(cfg.inventory || {}),
              enabled: true,
              lowStockThreshold:
                values.lowStockThreshold ?? cfg.inventory?.lowStockThreshold ?? 5,
              multiSitesEnabled:
                values.salesSiteMode === 'any_site'
                  ? true
                  : values.multiSitesEnabled ?? cfg.inventory?.multiSitesEnabled ?? false,
              salesSiteMode: values.salesSiteMode === 'any_site' ? 'any_site' : 'pos_only',
            };
          }
          configuration.expenses = {
            categories: expenseCategories?.categories ?? cfg.expenses?.categories ?? [],
          };
          configuration.salesActivity = {
            ...cfg.salesActivity,
            clientTypes: Array.isArray(clientTypes) ? clientTypes : [],
            opportunityCategories: Array.isArray(opportunityCategories)
              ? opportunityCategories
              : [],
          };
      } else if (section === 'loyalty') {
        const nextLoyaltyEnabled =
          values.loyaltyEnabled !== undefined
            ? values.loyaltyEnabled
            : cfg.loyalty?.enabled ?? true;
        if (nextLoyaltyEnabled && isFeatureEnabled(business, 'loyaltyPass')) {
          message.error(
            'Impossible d’activer la cagnotte : la carte de fidélité BonSpot est active. Désactivez d’abord la feature « Carte de fidélité BonSpot ».'
          );
          return;
        }
        configuration.loyalty = {
          ...(cfg.loyalty || {}),
          enabled: nextLoyaltyEnabled,
          bonusPercentage: values.bonusPercentage ?? cfg.loyalty?.bonusPercentage ?? 3,
          minimumUsage: values.minimumUsage ?? cfg.loyalty?.minimumUsage ?? 1000,
        };
      } else if (section === 'marketing') {
        configuration.marketing = {
          ...(cfg.marketing || {}),
          maxActiveCampaigns:
            values.maxActiveCampaigns !== undefined && values.maxActiveCampaigns !== null
              ? values.maxActiveCampaigns
              : cfg.marketing?.maxActiveCampaigns ?? 10,
        };
      } else if (section === 'messagesClients') {
        configuration.messagesClients = {
          ...(cfg.messagesClients || {}),
          delayMinutes:
            values.messagesClientsDelayMinutes ?? cfg.messagesClients?.delayMinutes ?? 15,
          antiSpamHours:
            values.messagesClientsAntiSpamHours ?? cfg.messagesClients?.antiSpamHours ?? 24,
          requireOptIn:
            values.messagesClientsRequireOptIn ?? cfg.messagesClients?.requireOptIn ?? true,
          channel: 'whatsapp',
        };
      } else if (section === 'inventory' && showInventoryConfig) {
        configuration.inventory = {
          ...(cfg.inventory || {}),
          enabled: true,
          lowStockThreshold: values.lowStockThreshold ?? cfg.inventory?.lowStockThreshold ?? 5,
          multiSitesEnabled:
            values.salesSiteMode === 'any_site'
              ? true
              : values.multiSitesEnabled ?? cfg.inventory?.multiSitesEnabled ?? false,
          salesSiteMode: values.salesSiteMode === 'any_site' ? 'any_site' : 'pos_only',
        };
      } else if (section === 'expenses') {
        configuration.expenses = {
          categories: expenseCategories?.categories ?? cfg.expenses?.categories ?? [],
        };
      } else if (section === 'clientTypes') {
        configuration.salesActivity = {
          ...cfg.salesActivity,
          clientTypes: Array.isArray(clientTypes) ? clientTypes : [],
        };
      } else if (section === 'opportunityCategories') {
        configuration.salesActivity = {
          ...cfg.salesActivity,
          opportunityCategories: Array.isArray(opportunityCategories)
            ? opportunityCategories
            : [],
        };
      } else if (section === 'activityTypes') {
        applyActivityTypes();
      }

      await updateBusinessMutation.mutateAsync({
        id: business.id,
        configuration: cleanFirestoreData(configuration),
      });
      message.success('Configuration mise à jour');
      await Promise.resolve(onSuccess());
      onClose();
      form.resetFields();
    } catch (error: any) {
      if (error.errorFields) return;
      message.error(error.message || 'Erreur lors de la mise à jour');
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  if (!business) return null;

  return (
    <Drawer
      title={
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <SettingOutlined style={{ color: '#722ed1' }} />
          {SECTION_TITLES[section]}
        </span>
      }
      open={open}
      onClose={handleCancel}
      destroyOnClose
      width={640}
      extra={
        <Space>
          <Button onClick={handleCancel}>Annuler</Button>
          <Button type="primary" loading={updateBusinessMutation.isPending} onClick={handleSubmit}>
            Enregistrer
          </Button>
        </Space>
      }
    >
      <Form form={form} layout="vertical" style={{ marginTop: 8 }}>
        {showActivityTypes && isFullEdit && (
          <>
            <Title level={5}>Rapports d&apos;activité</Title>
            <Divider />
          </>
        )}

        {showLoyalty && (
          <>
            {isFullEdit && <Title level={5}>Programme de fidélité</Title>}
            {isFeatureEnabled(business, 'loyaltyPass') && (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 12 }}
                message="Carte BonSpot active"
                description="La cagnotte FCFA et la carte BonSpot ne peuvent pas être actives en même temps. Désactivez la feature « Carte de fidélité BonSpot » avant d’activer la cagnotte."
              />
            )}
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={24}>
                <Form.Item
                  name="loyaltyEnabled"
                  label="Activer le programme de fidélité (cagnotte)"
                  valuePropName="checked"
                >
                  <Switch
                    checkedChildren="Activé"
                    unCheckedChildren="Désactivé"
                    disabled={isFeatureEnabled(business, 'loyaltyPass')}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="bonusPercentage" label="Pourcentage de bonus (%)">
                  <InputNumber min={0} max={20} step={0.5} style={{ width: '100%' }} addonAfter="%" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="minimumUsage" label="Seuil d'utilisation (FCFA)">
                  <InputNumber
                    min={500}
                    max={10000}
                    step={500}
                    style={{ width: '100%' }}
                    addonAfter="FCFA"
                  />
                </Form.Item>
              </Col>
            </Row>
          </>
        )}

        {showMarketing && (
          <>
            {isFullEdit && <Title level={5}>Marketing</Title>}
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={12}>
                <Form.Item name="maxActiveCampaigns" label="Campagnes actives max">
                  <InputNumber min={1} max={50} style={{ width: '100%' }} placeholder="10" />
                </Form.Item>
              </Col>
            </Row>
          </>
        )}

        {showMessages && (
          <>
            {isFullEdit && <Title level={5}>Messages Clients</Title>}
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={12}>
                <Form.Item name="messagesClientsDelayMinutes" label="Délai avant envoi (minutes)">
                  <InputNumber min={1} max={180} style={{ width: '100%' }} addonAfter="min" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="messagesClientsAntiSpamHours" label="Anti-spam (heures)">
                  <InputNumber min={1} max={168} style={{ width: '100%' }} addonAfter="h" />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  name="messagesClientsRequireOptIn"
                  label="Exiger l'opt-in client"
                  valuePropName="checked"
                >
                  <Switch checkedChildren="Oui" unCheckedChildren="Non" />
                </Form.Item>
              </Col>
            </Row>
          </>
        )}

        {showInventory && (
          <>
            {isFullEdit && <Title level={5}>Gestion des stocks</Title>}
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={24}>
                <Alert
                  type="info"
                  showIcon
                  message="Paramètres du stock"
                  description="Le module Stock / Inventaire s’active via les features du business. Ici, vous configurez uniquement son comportement."
                />
              </Col>
              <Col span={12}>
                <Form.Item name="lowStockThreshold" label="Seuil d'alerte stock faible">
                  <InputNumber min={1} max={100} style={{ width: '100%' }} addonAfter="unités" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Alert
                  type="success"
                  showIcon
                  message="Activation pilotée par les features"
                  description="Si la feature Stock / Inventaire est active, ces paramètres sont pris en compte immédiatement côté app."
                />
              </Col>
              <Col span={24}>
                <Form.Item
                  name="recalculateCostOnProvisioning"
                  label="Recalcul automatique du prix d'achat (désactivé)"
                  valuePropName="checked"
                  tooltip="Le recalcul automatique à la livraison est désactivé globalement."
                >
                  <Switch checkedChildren="Oui" unCheckedChildren="Non" disabled />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  name="multiSitesEnabled"
                  label="Stock multi-sites (magasin + boutique)"
                  valuePropName="checked"
                  tooltip="Permet de gérer plusieurs lieux. Les lieux se configurent dans l'onglet Lieux."
                  extra={
                    !inventoryFeatureOn
                      ? 'Recommandé : activer aussi la feature Stock / Inventaire.'
                      : undefined
                  }
                >
                  <Switch checkedChildren="Activé" unCheckedChildren="Désactivé" />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  name="salesSiteMode"
                  label="Où peut-on vendre ?"
                  tooltip="Boutique seule : ventes depuis le point de vente. Tous les lieux : choix du lieu à chaque vente."
                  extra="Par défaut : boutique seule — aucun changement pour les clients classiques."
                >
                  <Select
                    options={[
                      {
                        value: 'pos_only',
                        label: 'Boutique seule — vente uniquement depuis le point de vente',
                      },
                      {
                        value: 'any_site',
                        label:
                          'Tous les lieux — vente depuis n’importe quel lieu actif (terrain / multi-dépôts)',
                      },
                    ]}
                  />
                </Form.Item>
                {anySiteSelected && (!multiSitesOn || !inventoryFeatureOn || !posFeatureOn) && (
                  <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message="Prérequis recommandés pour « Tous les lieux »"
                    description={
                      <ul style={{ margin: 0, paddingLeft: 18 }}>
                        {!multiSitesOn && <li>Activer « Stock multi-sites »</li>}
                        {!inventoryFeatureOn && (
                          <li>Feature contrat « Stock / Inventaire »</li>
                        )}
                        {!posFeatureOn && <li>Feature contrat « Point de vente » (pos)</li>}
                      </ul>
                    }
                  />
                )}
              </Col>
            </Row>
          </>
        )}

        {showExpenses && (
          <>
            {isFullEdit && <Divider />}
            <ExpenseCategoriesStep
              value={expenseCategoriesValue ?? { categories: [] }}
              onChange={(value) => form.setFieldsValue({ expenseCategories: value })}
              businessId={business.id}
            />
          </>
        )}
        {showClientTypes && (
          <ClientTypesStep
            value={clientTypesValue}
            onChange={(value) => form.setFieldsValue({ clientTypes: value })}
          />
        )}
        {showOpportunityCategories && (
          <OpportunityCategoriesStep
            value={opportunityCategoriesValue}
            onChange={(value) => form.setFieldsValue({ opportunityCategories: value })}
          />
        )}
        {showActivityTypes && (
          <ActivityTypesStep
            value={activityTypesValue}
            onChange={(value) => form.setFieldsValue({ activityTypes: value })}
          />
        )}
      </Form>
    </Drawer>
  );
};

export default EditConfigurationModal;
